import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import {
    setShowRecurrence,
    resetScheduledMessageEditor,
} from '@common/reducers/ScheduledMessageEditorReducer';
import { RetryLimitUnit } from '@common/types/interface';
import { useEffect, useRef } from 'react';
import ScheduleSettings from './schedule-picker';

export type SchedulePickerState = {
    dueDate: string;
    rruleset: string;
    isRecurring: boolean;
    cancelIfReceived: boolean;
    retryLimitDuration?: number;
    retryLimitUnit?: RetryLimitUnit;
};

type SchedulePickerStandaloneWrapperProps = {
    // Configuration
    showRecurrence?: boolean;
    showDeliveryOptions?: boolean;
    /** FE#240: restrict the recurrence frequency dropdown (default: all). */
    allowedFrequencies?: import('rrule').Frequency[];
    // Callback to get final state before cleanup
    onUnmount?: (state: SchedulePickerState) => void;
}

export default function SchedulePickerStandaloneWrapper({
    showRecurrence = false,
    showDeliveryOptions = false,
    allowedFrequencies,
    onUnmount,
}: SchedulePickerStandaloneWrapperProps) {
    const dispatch = useAppDispatch();
    
    // Get current state from ScheduledMessageEditorReducer
    const editorState = useAppSelector((state: any) => state.ScheduledMessageEditorReducer);
    
    // Use refs to store latest values to avoid closure issues
    const onUnmountRef = useRef(onUnmount);
    const editorStateRef = useRef(editorState);
    
    // Keep refs updated
    onUnmountRef.current = onUnmount;
    editorStateRef.current = editorState;

    // Initialize Redux state for this standalone usage
    useEffect(() => {
        dispatch(setShowRecurrence(showRecurrence));
    }, [dispatch, showRecurrence]);

    // Clean up Redux state when component unmounts - callback first, then reset
    // Only run on mount/unmount, not when editorState changes
    useEffect(() => {
        return () => {
            // Call callback with current state BEFORE resetting
            // Use ref to get latest state value
            const currentState = editorStateRef.current;
            if (onUnmountRef.current && currentState) {
                const finalState: SchedulePickerState = {
                    dueDate: currentState?.dueDate || '',
                    rruleset: currentState?.rruleset || '',
                    isRecurring: currentState?.isRecurring || false,
                    cancelIfReceived: currentState?.cancelIfReceived || false,
                    retryLimitDuration: currentState?.retryLimitDuration,
                    retryLimitUnit: currentState?.retryLimitUnit,
                };
                onUnmountRef.current(finalState);
            }
            
            // Then reset Redux state to prevent conflicts
            dispatch(resetScheduledMessageEditor());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]); // Only depend on dispatch, not editorState or onUnmount

    // Render the pure component
    return <ScheduleSettings showDeliveryOptions={showDeliveryOptions} allowedFrequencies={allowedFrequencies} />;
} 